import React, { useState } from 'react';
import { Calendar, Search, Plus, Clock, MapPin, FileText, Users, Rocket, Stethoscope } from 'lucide-react';

const EventManagement = () => {
  const [selectedDate, setSelectedDate] = useState('Aug 29, 2025 — Aug 29, 2025');
  const [currentMonth, setCurrentMonth] = useState('August');
  const [currentYear, setCurrentYear] = useState(2025);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Sample events data
  const events = [
    {
      id: 1,
      title: 'Team Meeting',
      type: 'MEETING',
      date: '8/28/2025',
      time: '09:53 AM',
      duration: '1h',
      location: 'Conference Room A',
      description: 'Weekly team sync and project updates',
      status: 'completed',
      icon: Users,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 2,
      title: 'Product Launch',
      type: 'EVENT',
      date: '8/30/2025',
      time: '09:53 AM',
      duration: '4h',
      location: 'Main Auditorium',
      description: 'Launch of our new product line',
      status: 'upcoming',
      icon: Rocket,
      color: 'bg-orange-100 text-orange-600'
    },
    {
      id: 3,
      title: 'Doctor Appointment',
      type: 'APPOINTMENT',
      date: '9/3/2025',
      time: '09:53 AM',
      duration: '1h',
      location: 'Medical Center',
      description: 'Annual check-up',
      status: 'upcoming',
      icon: Stethoscope,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 4,
      title: 'Client Presentation',
      type: 'MEETING',
      date: '9/5/2025',
      time: '02:00 PM',
      duration: '2h',
      location: 'Boardroom',
      description: 'Quarterly business review with key clients',
      status: 'upcoming',
      icon: Users,
      color: 'bg-green-100 text-green-600'
    },
        {
      id: 4,
      title: 'Client Presentation',
      type: 'MEETING',
      date: '9/5/2025',
      time: '02:00 PM',
      duration: '2h',
      location: 'Boardroom',
      description: 'Quarterly business review with key clients',
      status: 'upcoming',
      icon: Users,
      color: 'bg-green-100 text-green-600'
    }
  ];

  // Calendar data
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  
  const currentMonthIndex = months.indexOf(currentMonth);
  const daysInMonth = getDaysInMonth(currentMonthIndex, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonthIndex, currentYear);

  const calendarDays = [];
  
  // Previous month's trailing days
  for (let i = firstDay - 1; i >= 0; i--) {
    const prevMonth = currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
    const prevYear = currentMonthIndex === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = getDaysInMonth(prevMonth, prevYear);
    calendarDays.push({
      day: daysInPrevMonth - i,
      isCurrentMonth: false,
      isToday: false
    });
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday = day === 29; // Highlighting Aug 29 as today
    calendarDays.push({
      day,
      isCurrentMonth: true,
      isToday
    });
  }

  // Next month's leading days
  const totalCells = 42;
  const remainingCells = totalCells - calendarDays.length;
  for (let day = 1; day <= remainingCells; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      isToday: false
    });
  }

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const upcomingEvents = events.filter(event => event.status === 'upcoming');
  const thisWeekEvents = events.filter(event => 
    event.date === '8/30/2025' || event.date === '8/31/2025' || event.date === '9/1/2025'
  );
  const pastEvents = events.filter(event => event.status === 'completed');

  const handleRowClick = (event) => {
    setSelectedEvent(selectedEvent?.id === event.id ? null : event);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl ">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Calendar className="text-red-500 w-8 h-8" />
          <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Calendar and Stats */}
          <div className="space-y-6 ">
            {/* Date Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-gray-600">Date Filter</span>
                <button className="text-sm text-blue-600 hover:text-blue-800">Clear</button>
              </div>
              <div className="text-sm text-gray-800 bg-gray-100 p-2 rounded">
                {selectedDate}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <button className="p-1 hover:bg-gray-100 rounded">
                  <span className="text-gray-600">‹</span>
                </button>
                <div className="flex items-center gap-2">
                  <select 
                    value={currentMonth} 
                    onChange={(e) => setCurrentMonth(e.target.value)}
                    className="text-lg font-semibold bg-transparent border-none outline-none"
                  >
                    {months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                  <select 
                    value={currentYear} 
                    onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                    className="text-lg font-semibold bg-transparent border-none outline-none"
                  >
                    <option value={2024}>2024</option>
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                  </select>
                </div>
                <button className="p-1 hover:bg-gray-100 rounded">
                  <span className="text-gray-600">›</span>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-xs text-gray-500 text-center p-2 font-medium">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <button
                    key={index}
                    className={`
                      text-sm p-2 rounded hover:bg-gray-100 transition-colors
                      ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-300'}
                      ${day.isToday ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                    `}
                  >
                    {day.day}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mt-4 text-sm">
                <button className="px-3 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                  Last 7 days
                </button>
                <button className="px-3 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                  Last 30 days
                </button>
              </div>
            </div>

          </div>

          {/* Right Column - Events Table and Upcoming Events */}
          <div className="lg:col-span-2 space-y-6 " >
           
            
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">3</div>
                <div className="text-sm text-blue-600">TOTAL EVENTS</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">2</div>
                <div className="text-sm text-purple-600">UPCOMING</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600">1</div>
                <div className="text-sm text-green-600">PAST EVENTS</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">2</div>
                <div className="text-sm text-orange-600">THIS WEEK</div>
              </div>
            </div>

            {/* Upcoming Events Sidebar */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="text-red-500 w-5 h-5" />
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
              </div>
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div key={event.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`p-2 rounded-lg ${event.color}`}>
                      <event.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{event.title}</h4>
                      <p className="text-sm text-gray-500">{event.date} at {event.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>


                    {/* Search and Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm mt-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>All Types</option>
                  <option>Meeting</option>
                  <option>Event</option>
                  <option>Appointment</option>
                </select>
                <select className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>All Status</option>
                  <option>Upcoming</option>
                  <option>Completed</option>
                </select>
                <button className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 transition-colors">
                  <Plus className="w-4 h-4" />
                  Add Event
                </button>
              </div>
            </div>

            {/* Events Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEvents.map((event) => (
                      <React.Fragment key={event.id}>
                        <tr 
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                            selectedEvent?.id === event.id ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => handleRowClick(event)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${event.color}`}>
                                <event.icon className="w-4 h-4" />
                              </div>
                              <span className="font-medium text-gray-900">{event.title}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${event.color}`}>
                              {event.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {event.date} at {event.time}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-gray-400" />
                              {event.duration}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {event.location}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              event.status === 'upcoming' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {event.status}
                            </span>
                          </td>
                        </tr>
                        {selectedEvent?.id === event.id && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900">Event Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-600">Description:</span>
                                    <p className="text-gray-800 mt-1">{event.description}</p>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">Full Schedule:</span>
                                    <p className="text-gray-800 mt-1">
                                      {event.date} from {event.time} ({event.duration})
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-2 pt-2">
                                  <button className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-600 transition-colors">
                                    ✓ Edit
                                  </button>
                                  <button className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-600 transition-colors">
                                    ✗ Cancel
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>




      </div>
    </div>
  );
};

export default EventManagement;